import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { scheduledDashboardAPI } from '../../../lib/api/scheduled-dashboard';
import { KPICell, SectionFrame, type PeriodContentProps } from './shared';
import { ForecastDrilldownModal, type DrilldownMode } from './ForecastDrilldownModal';
import { SupplierPartsCard } from './SupplierPartsCard';
import { useFormatters } from '../../../contexts/PreferencesContext';

const PALETTE = {
  firm:     '#0f766e',  // teal — committed window
  forecast: '#3b82f6',  // blue — beyond firm
  ringDark: '#1e293b',
};

// Distinct hues for the by-supplier stack. Cycled if a part somehow has more
// forward suppliers than colours — the legend still names each one.
const SUPPLIER_COLOURS = [
  '#0f766e', '#3b82f6', '#b45309', '#7c3aed',
  '#be123c', '#0891b2', '#4d7c0f', '#a21caf',
];

export function Forecast({ data, formatCurrency, filters }: PeriodContentProps) {
  const { formatQuantity } = useFormatters();
  const { firm, forecast, default_firm_days } = data;
  const [drilldown, setDrilldown] = useState<DrilldownMode | null>(null);
  const [splitMode, setSplitMode] = useState<'bucket' | 'supplier'>('bucket');
  const [exporting, setExporting] = useState(false);

  /**
   * Excel export of the forward pipeline. `all` drops the on-screen filters
   * server-side; the filtered form mirrors exactly what the tab is showing.
   */
  // The shell's filter scope carries ids as number|string; the API takes
  // numbers.
  const asId = (v: number | string | undefined) =>
    v === undefined || v === '' ? undefined : Number(v);

  const handleExport = async (exportAll: boolean) => {
    setExporting(true);
    try {
      const res = await scheduledDashboardAPI.exportForecast({
        ...(exportAll ? {} : {
          supplier_id: asId(filters?.supplier_id),
          part_number: filters?.part_number,
        }),
        // Scope rather than content filter — kept on both paths so a
        // spot-order export stays spot and stays in the right tenant.
        customer_id: asId(filters?.customer_id),
        erp_order_type: filters?.erp_order_type,
        ...(exportAll ? { export_all: true } : {}),
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `forecast_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(exportAll ? 'Exported full forecast.' : 'Exported filtered forecast.');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Forecast export failed: ${msg}`);
    } finally {
      setExporting(false);
    }
  };
  const todayIso = new Date().toISOString().slice(0, 10);
  const drillFilters = filters ?? { erp_order_type: '4' as const };
  const totalSchedules = firm.schedules + forecast.schedules;
  const totalOpenQty   = firm.open_qty + forecast.open_qty;
  const totalOpenValue = firm.open_value + forecast.open_value;
  // Pipeline tiles read on the OPEN basis so they reconcile exactly with the
  // firm/forecast cards above (which headline open_qty / open_value). The
  // committed basis (total_qty / total_value) stays on those cards' footers.
  const firmValuePct   = totalOpenValue ? (firm.open_value / totalOpenValue) * 100 : 0;


  // Stacked column per delivery week. Weeks come from the server already
  // bucketed on the tenant's week_start_day, so no date maths here.
  const weekChart = (data.forecast_weeks ?? []).map(w => {
    const d = new Date(`${w.week_start}T00:00:00`);
    return {
      label: d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' }),
      fullLabel: `Week of ${d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}`,
      firm: w.firm_qty,
      forecast: w.forecast_qty,
      firmValue: w.firm_value,
      forecastValue: w.forecast_value,
      ...(w.by_supplier ?? {}),
    };
  });

  // Only offer the supplier split when the qty is genuinely shared — a single
  // supplier has nothing to split, and the server already excludes suppliers
  // with no forward qty.
  const weekSuppliers = data.forecast_week_suppliers ?? [];
  const canSplitBySupplier = weekSuppliers.length > 1;
  const splitBySupplier = canSplitBySupplier && splitMode === 'supplier';

  const valueDonut = [
    { name: 'Firm window', value: firm.open_value,     color: PALETTE.firm },
    { name: 'Forecast',    value: forecast.open_value, color: PALETTE.forecast },
  ];

  return (
    <div className="space-y-6">
      {/* Section frames — same visual language as other sub-tabs.
          Each tile is clickable: opens a drilldown modal scoped to the
          tile's segment (firm / forecast) plus the dashboard filter
          context (customer / supplier / part / erp_order_type).
       */}
      <div className="grid grid-cols-2 gap-4">
        <button
          type="button"
          onClick={() => setDrilldown('firm')}
          className="text-left transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-300 rounded-lg"
          aria-label="Drill down into firm-window schedules"
        >
          <SectionFrame
            title="Firm Window"
            accent="green"
            subtitle={
              default_firm_days > 0
                ? `next ${default_firm_days}d default · per-part*`
                : 'per-part* · no default'
            }
          >
            <div className="grid grid-cols-3 gap-3 items-start">
              <KPICell
                label="Schedules"
                value={<span className="text-lg font-semibold tabular-nums text-slate-900">{formatQuantity(firm.schedules)}</span>}
              />
              <KPICell
                label="Open Qty"
                value={<span className="text-lg font-semibold tabular-nums text-slate-900">{formatQuantity(firm.open_qty)}</span>}
                foot={`of ${formatQuantity(firm.total_qty)}`}
              />
              <KPICell
                label="Open Value"
                value={<span className="text-lg font-semibold tabular-nums text-slate-900">{formatCurrency(firm.open_value)}</span>}
                foot={`${formatCurrency(firm.total_value)} total`}
              />
            </div>
          </SectionFrame>
        </button>

        <button
          type="button"
          onClick={() => setDrilldown('forecast')}
          className="text-left transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-300 rounded-lg"
          aria-label="Drill down into forecast schedules beyond the firm window"
        >
          <SectionFrame title="Forecast — Beyond Firm" accent="blue" subtitle="future commitments">
            <div className="grid grid-cols-3 gap-3 items-start">
              <KPICell
                label="Schedules"
                value={<span className="text-lg font-semibold tabular-nums text-slate-900">{formatQuantity(forecast.schedules)}</span>}
              />
              <KPICell
                label="Open Qty"
                value={<span className="text-lg font-semibold tabular-nums text-slate-900">{formatQuantity(forecast.open_qty)}</span>}
                foot={`of ${formatQuantity(forecast.total_qty)}`}
              />
              <KPICell
                label="Open Value"
                value={<span className="text-lg font-semibold tabular-nums text-slate-900">{formatCurrency(forecast.open_value)}</span>}
                foot={`${formatCurrency(forecast.total_value)} total`}
              />
            </div>
          </SectionFrame>
        </button>

      </div>

      <ForecastDrilldownModal
        open={drilldown !== null}
        mode={drilldown ?? 'all'}
        onClose={() => setDrilldown(null)}
        filters={drillFilters}
        todayIso={todayIso}
        defaultFirmDays={default_firm_days}
      />

      {/* Qty due per delivery week, split firm/forecast. Answers "when are
          the deliveries expected" directly — line counts said nothing about
          how much was coming, and required opening the POs to find out.
          Bars sum to the Open Qty tiles above. */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-4">
            <div>
              <CardTitle className="text-sm font-medium text-slate-900">
                Qty Due by Week — {splitBySupplier ? 'by Supplier' : 'Firm vs Forecast'}
              </CardTitle>
              <p className="text-[11px] text-slate-900">
                {weekChart.length > 0
                  ? `Outstanding qty by delivery week · ${formatQuantity(totalOpenQty)} across ${weekChart.length} week${weekChart.length === 1 ? '' : 's'}`
                  : 'No forward deliveries scheduled'}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {/* Only shown when the qty is actually shared between suppliers. */}
              {canSplitBySupplier && (
                <div className="flex rounded-md border border-slate-200 p-0.5">
                  {([
                    ['bucket', 'Firm / Forecast'],
                    ['supplier', `By supplier (${weekSuppliers.length})`],
                  ] as const).map(([mode, label]) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setSplitMode(mode)}
                      className={`rounded px-2 py-1 text-[11px] font-medium transition-colors ${
                        splitMode === mode
                          ? 'bg-slate-900 text-white'
                          : 'text-slate-900 hover:bg-slate-100'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    disabled={exporting}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] font-medium text-slate-900 hover:bg-slate-100 disabled:opacity-50"
                  >
                    {exporting
                      ? <Loader2 className="h-3 w-3 animate-spin" />
                      : <Download className="h-3 w-3" />}
                    Export
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleExport(false)}>
                    Export filtered forecast
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleExport(true)}>
                    Export full forecast
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {weekChart.length === 0 ? (
            <div className="flex h-56 items-center justify-center text-sm text-slate-900">
              Nothing due — no forward-dated schedules in scope.
            </div>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weekChart} margin={{ top: 5, right: 16, bottom: 0, left: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10, fill: '#0f172a' }}
                    axisLine={false}
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fontSize: 10, fill: '#0f172a' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 6 }}
                    labelFormatter={(label, items) => {
                      const p = items?.[0]?.payload as { fullLabel?: string } | undefined;
                      return p?.fullLabel ?? String(label);
                    }}
                    formatter={(value, name, item) => {
                      const v = Number(value) || 0;
                      if (splitBySupplier) return [formatQuantity(v), name];
                      const p = item?.payload as { firmValue?: number; forecastValue?: number } | undefined;
                      const val = name === 'Firm window' ? p?.firmValue : p?.forecastValue;
                      return [
                        `${formatQuantity(v)}${val ? ` · ${formatCurrency(val)}` : ''}`,
                        name,
                      ];
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  {splitBySupplier
                    ? weekSuppliers.map((name, i) => (
                        <Bar
                          key={name}
                          dataKey={name}
                          stackId="w"
                          fill={SUPPLIER_COLOURS[i % SUPPLIER_COLOURS.length]}
                          name={name}
                          radius={i === weekSuppliers.length - 1 ? [3, 3, 0, 0] : undefined}
                        />
                      ))
                    : (
                      <>
                        <Bar dataKey="firm"     stackId="w" fill={PALETTE.firm}     name="Firm window" />
                        <Bar dataKey="forecast" stackId="w" fill={PALETTE.forecast} name="Forecast" radius={[3, 3, 0, 0]} />
                      </>
                    )}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Only when a supplier is filtered — the per-part detail the strip
          cannot carry for a multi-part supplier. */}
      <SupplierPartsCard
        parts={data.supplier_parts ?? []}
        supplierName={data.supplier_info?.supplier}
        totalParts={data.supplier_info?.parts}
        customerId={asId(filters?.customer_id)}
        supplierId={asId(filters?.supplier_id)}
        erpOrderType={filters?.erp_order_type}
      />

      <div className="grid grid-cols-2 gap-4">
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-900">Open Value Mix</CardTitle>
            <p className="text-[11px] text-slate-900">
              Of {formatCurrency(totalOpenValue)} outstanding, {Math.round((firm.open_value / totalOpenValue) * 100 || 0)}% sits in the firm window
            </p>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={valueDonut}
                    dataKey="value"
                    nameKey="name"
                    cx="50%" cy="50%"
                    innerRadius={56}
                    outerRadius={84}
                    paddingAngle={2}
                    stroke="none"
                  >
                    {valueDonut.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} formatter={(v: number) => formatCurrency(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-900">Pipeline at a Glance</CardTitle>
            <p className="text-[11px] text-slate-900">All forward-dated, real schedules (excl. line 0)</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 py-2">
              <div>
                <p className="text-[10px] text-slate-900 font-medium uppercase tracking-wide">Forward Schedules</p>
                <p className="text-2xl font-semibold mt-1 tabular-nums">{totalSchedules.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-900 font-medium uppercase tracking-wide">Open Qty</p>
                <p className="text-2xl font-semibold mt-1 tabular-nums">{formatQuantity(totalOpenQty)}</p>
                <p className="text-[10px] text-slate-900 mt-1">balance still owed</p>
              </div>
              <div className="col-span-2">
                <p className="text-[10px] text-slate-900 font-medium uppercase tracking-wide">Total Pipeline Value</p>
                <p className="text-2xl font-semibold mt-1 tabular-nums">{formatCurrency(totalOpenValue)}</p>
                <p className="text-[10px] text-slate-900 mt-1">{Math.round(firmValuePct)}% in firm window</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <p className="text-[10px] text-slate-900">
        *Firm window is per-part — derived from <code>PartScheduleEntry.schedule_firm</code>.
        {default_firm_days > 0 ? (
          <>
            {' '}A <strong>{default_firm_days}-day</strong> default applies when a part has no entry.
          </>
        ) : (
          <>
            {' '}Parts without an entry have <strong>no firm window</strong> — every future delivery counts as forecast.
          </>
        )}
      </p>
    </div>
  );
}
